import React from 'react';
import { AppSpec } from '../types';
import '../styles/AppPreview.css';

interface Props {
  spec: AppSpec;
}

const AppPreview: React.FC<Props> = ({ spec }) => {
  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'must': return '#e74c3c';
      case 'should': return '#f39c12';
      case 'could': return '#3498db';
      default: return '#95a5a6';
    }
  };

  return (
    <div className="app-preview">
      <h2>Preview</h2>
      <p className="subtitle">Review your app specification before generating</p>

      <div className="preview-sections">
        {/* App Info Card */}
        <section className="preview-section">
          <h3>App Information</h3>
          <div className="preview-card portal-card-preview">
            <div className="card-icon">{spec.icon}</div>
            <div className="card-content">
              <h4>{spec.displayName || 'Untitled App'}</h4>
              <p>{spec.description || 'No description'}</p>
              <span className="card-url">https://{spec.subdomain || 'my-app'}.sae-g.com</span>
            </div>
          </div>
        </section>

        {/* Skills Overview */}
        <section className="preview-section">
          <h3>Selected Skills ({spec.skills.length})</h3>
          {spec.skills.length > 0 ? (
            <div className="skills-preview">
              {spec.skills.map(skill => (
                <div key={skill.id} className="skill-preview-item">
                  <span className="skill-name">{skill.name}</span>
                  <span className="skill-category">{skill.category}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-preview">No skills selected</p>
          )}
        </section>

        {/* Requirements Overview */}
        <section className="preview-section">
          <h3>Requirements ({spec.requirements.length})</h3>
          {spec.requirements.length > 0 ? (
            <div className="requirements-preview">
              {spec.requirements.map(req => (
                <div key={req.id} className="requirement-preview-item">
                  <span
                    className="requirement-priority"
                    style={{ backgroundColor: getPriorityColor(req.priority) }}
                  >
                    {req.priority.toUpperCase()}
                  </span>
                  <span className="requirement-title">{req.title}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-preview">No requirements defined</p>
          )}
        </section>

        {/* Steering Configuration */}
        <section className="preview-section">
          <h3>Configuration</h3>
          <div className="config-preview">
            <div className="config-item">
              <span className="config-label">Backend:</span>
              <span className="config-value">
                {spec.steering.hasBackend
                  ? `${spec.steering.backendType} with ${spec.steering.dataStorage}`
                  : 'No backend'}
              </span>
            </div>
            <div className="config-item">
              <span className="config-label">Authentication:</span>
              <span className="config-value">
                {spec.steering.authRequired ? 'Required' : 'Public'}
              </span>
            </div>
            {spec.steering.authRequired && (
              <div className="config-item">
                <span className="config-label">Allowed Groups:</span>
                <span className="config-value">
                  {spec.steering.allowedGroups.join(', ')}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* Generated Structure Preview */}
        <section className="preview-section">
          <h3>Generated Structure</h3>
          <div className="structure-preview">
            <pre>{`${spec.subdomain || 'my-app'}/
├── frontend/
│   ├── src/
│   │   ├── components/
${spec.skills.filter(s => s.category === 'frontend').map(s => `│   │   │   ├── ${s.name.replace(/\s+/g, '')}.tsx`).join('\n')}
│   │   ├── hooks/
│   │   ├── types/
│   │   ├── utils/
│   │   ├── App.tsx
│   │   └── index.tsx
│   ├── public/
│   └── package.json
${spec.steering.hasBackend ? `├── backend/
│   ├── ${spec.steering.backendType === 'nodejs' ? 'src/' : 'handlers/'}
${spec.skills.filter(s => s.category === 'backend').map(s => `│   │   ├── ${s.name.replace(/\s+/g, '').toLowerCase()}.${spec.steering.backendType === 'nodejs' ? 'js' : 'py'}`).join('\n')}
│   └── ${spec.steering.backendType === 'nodejs' ? 'package.json' : 'requirements.txt'}
` : ''}└── README.md`}</pre>
          </div>
        </section>

        {/* Deployment Info */}
        <section className="preview-section">
          <h3>Deployment</h3>
          <div className="deployment-preview">
            <div className="deployment-item">
              <span className="deployment-icon">🪣</span>
              <div className="deployment-info">
                <strong>S3 Bucket</strong>
                <span>{spec.subdomain || 'my-app'}-frontend-dev</span>
              </div>
            </div>
            <div className="deployment-item">
              <span className="deployment-icon">☁️</span>
              <div className="deployment-info">
                <strong>CloudFront</strong>
                <span>{spec.subdomain || 'my-app'}.sae-g.com</span>
              </div>
            </div>
            <div className="deployment-item">
              <span className="deployment-icon">🚪</span>
              <div className="deployment-info">
                <strong>Portal Card</strong>
                <span>Will be added to EA Portal</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AppPreview;
